import React, { useCallback } from 'react'
import { IonButton, IonCol, IonIcon, IonLabel, IonRow } from '@ionic/react'
import { addCircleOutline, arrowDown, help, person } from 'ionicons/icons'
import { useTranslation } from 'react-i18next'
import './PatientInfo.component.css'
import { PATIENT_MATCH_SUB_STEP_IDS, STEP_IDS } from '../../constants'
import { useDispatch, useSelector } from 'react-redux'
import {
  getPulsaraCurrentStep,
  getPulsaraCurrentSubStep,
  getPulsaraDetailsPatientDemographics,
  getPulsaraDetailsCreatedAt,
  getPulsaraDetailsCaseType,
  getImportSelectionsPatientDemographics,
  getPulsaraLoading,
} from '../../selectors'
import { formatDateTime } from '../../../../utilities/dateTime.util'
import { useOfflineStatus } from '../../../../utilities/isOnline.util'
import { initializePatientConflictFromPCR } from '../../store/pulsara.actions'

const PatientInfo: React.FC = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const currentStep = useSelector(getPulsaraCurrentStep)
  const currentSubStep = useSelector(getPulsaraCurrentSubStep)
  const pulsaraDetailsPatientDemographics = useSelector(getPulsaraDetailsPatientDemographics)
  const pulsaraDetailsCreatedAt = useSelector(getPulsaraDetailsCreatedAt)
  const pulsaraDetailsCaseType = useSelector(getPulsaraDetailsCaseType)
  const importSelectionsPatientDemographics = useSelector(getImportSelectionsPatientDemographics)
  const isOfflineStatus = useOfflineStatus()
  const loading = useSelector(getPulsaraLoading)

  const getDetails = useCallback(() => {
    const isOnImportStep = currentStep === STEP_IDS.IMPORT

    const firstName =
      isOnImportStep && importSelectionsPatientDemographics?.firstName
        ? importSelectionsPatientDemographics.firstName
        : pulsaraDetailsPatientDemographics?.firstName
    const lastName =
      isOnImportStep && importSelectionsPatientDemographics?.lastName
        ? importSelectionsPatientDemographics.lastName
        : pulsaraDetailsPatientDemographics?.lastName
    const createdAtLabel = pulsaraDetailsCreatedAt ? formatDateTime(pulsaraDetailsCreatedAt) : ''

    return { firstName, lastName, createdAtLabel, caseType: pulsaraDetailsCaseType }
  }, [
    currentStep,
    importSelectionsPatientDemographics,
    pulsaraDetailsPatientDemographics,
    pulsaraDetailsCreatedAt,
    pulsaraDetailsCaseType,
  ])

  const { firstName, lastName, createdAtLabel, caseType } = getDetails()
  const displayName = `${firstName ?? ''} ${lastName ?? ''}`.trim() || t('pulsara.NotAvailable')

  const onPatientClick = () => {
    dispatch(initializePatientConflictFromPCR())
  }

  if (loading === 'records') return <></>

  return (
    <IonRow className='pulsara-patient-info-container'>
      <IonCol
        size='auto'
        className={`pulsara-patient-info-left ${currentStep === STEP_IDS.IMPORT ? 'user-confirmed' : ''}`}>
        <div className='pulsara-patient-icon-wrapper'>
          <IonIcon icon={person} className='pulsara-patient-icon' />
          <IonIcon icon={currentStep === STEP_IDS.IMPORT ? arrowDown : help} className='pulsara-patient-help-icon' />
        </div>
        <div className='pulsara-patient-details'>
          <IonLabel className='pulsara-patient-name'>{displayName}</IonLabel>
          <IonLabel className='pulsara-patient-timestamp'>{createdAtLabel}</IonLabel>
        </div>
      </IonCol>
      <IonCol className='pulsara-patient-info-right'>
        {currentStep === STEP_IDS.PATIENT_MATCH ? (
          (currentSubStep === PATIENT_MATCH_SUB_STEP_IDS.PATIENT_SEARCH_RESULTS ||
            (isOfflineStatus && currentSubStep !== PATIENT_MATCH_SUB_STEP_IDS.PATIENT_CONFLICT)) && (
            <IonButton
              fill='clear'
              className='h-auto ion-no-padding pulsara-patient-add-button'
              onClick={onPatientClick}
              data-testid='PulsaraPatientButton'>
              <IonIcon icon={addCircleOutline} className='pulsara-patient-add-icon' />
              <IonLabel>{t('pulsara.Patient')}</IonLabel>
            </IonButton>
          )
        ) : (
          <IonLabel color='medium'>{caseType}</IonLabel>
        )}
      </IonCol>
    </IonRow>
  )
}

export default PatientInfo
